import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useResponsive } from '@features/responsive';
import { TSection, TText } from '@features/footer/typescript';
import styles from '@features/footer/Footer.styles';
import Heading from '@components/Heading/Heading';
import theme from '@styles/theme';
import Icon from '@components/icons/Icon';
import Button from '@components/Button/Button';
import StatutoryConsumerProtectionNotice from '@components/StatutoryConsumerProtectionNotice/StatutoryConsumerProtectionNotice';
const SHOPPING_SECTION_HEADER = /shopping at|zakupy na|voxfurniture\.com|einkauf|achat sur|cump[aă]r[aă]turi|пазаруване/i;
type PropsType = {
  section: TSection;
  index: number;
};
const GeneralList = ({
  section,
  index
}: PropsType) => {
  const {
    t
  } = useTranslation('layouts');
  const {
    isNarrowerThanTablet
  } = useResponsive();
  const [showItems, setShowItems] = useState<boolean>(false);
  const [mounted, setMounted] = useState<boolean>(false);
  const {
    header,
    items
  } = section;
  const isShoppingSection = SHOPPING_SECTION_HEADER.test(header);
  useEffect(() => {
    setMounted(true);
    if (!isNarrowerThanTablet) {
      setShowItems(true);
    }
  }, [isNarrowerThanTablet]);
  const parsedItems = useMemo(() => items.map((item: TText, idx: number) => ({
    ...item,
    id: idx
  })), [items]);
  const isExternalLink = (item: TText): boolean => !!item.html || /^(?:[a-z]+:)?\/\//i.test(item.link);
  const createHTMLTag = (item: TText) => {
    if (item.html) {
      const hasTargetBlank = item.html.includes('target="_blank"');
      if (hasTargetBlank) {
        const linkTextMatch = item.html.match(/<a[^>]*>([^<]+)<\/a>/);
        const linkText = linkTextMatch ? linkTextMatch[1] : item.value;
        const modifiedHtml = item.html.replace(/<a([^>]*target="_blank"[^>]*)>/, `<a$1 title="${linkText} ${t('footer.links.opensInNewWindow')}">`);
        return <div dangerouslySetInnerHTML={{
          __html: modifiedHtml
        }} />;
      }
      return <div dangerouslySetInnerHTML={{
        __html: item.html
      }} />;
    }
    return <a href={item.link} target="_blank" rel="noreferrer" title={`${item.value} ${t('footer.links.opensInNewWindow')}`}>
        {item.value}
      </a>;
  };
  const shouldShowToggle = !mounted ? true : isNarrowerThanTablet;
  const shouldShowItems = !mounted ? true : showItems || !isNarrowerThanTablet;
  return <div sx={styles.footerGeneralListContainer(index)}>
      {shouldShowToggle ? <div sx={styles.footerGeneralListToogleBlock} onClick={() => setShowItems(!showItems)} onKeyDown={() => setShowItems(!showItems)} aria-hidden="true">
          <div sx={styles.footerGeneralListWrapper}>
            <Heading h={4} headingType={4} mobile color={theme.rawColors.darkGrey700}>
              {header}
            </Heading>
            <Button variant="iconOnly" addSx={styles.addButton} cy="CloseButton" title={showItems ? t('footer.toggle.less') : t('footer.toggle.more')}>
              <Icon icon={showItems ? 'Minus' : 'Add'} size="18px" title={showItems ? t('footer.toggle.less') : t('footer.toggle.more')} />
            </Button>
          </div>
        </div> : <Heading h={4} headingType={4} mobile pb={16} color={theme.rawColors.darkGrey700}>
          {header}
        </Heading>}
      {shouldShowItems && <ul sx={styles.footerGeneralList}>
          {parsedItems.map((item: TText) => <li key={item.id}>
              {isExternalLink(item) ? <div sx={styles.footerGeneralListItem}>{createHTMLTag(item)}</div> : <Link href={item.link} className="footer_nav" sx={styles.footerNavLink}>
                  <div sx={styles.footerGeneralListItem}>{item.value}</div>
                </Link>}
            </li>)}
          {isShoppingSection && <StatutoryConsumerProtectionNotice renderTrigger={(open, title) => <li>
                  <button type="button" className="footer_nav" data-cy="StatutoryConsumerProtectionFooter" sx={styles.footerActionLink} onClick={open}>
                    <div sx={styles.footerGeneralListItem}>{title}</div>
                  </button>
                </li>} />}
        </ul>}
    </div>;
};
export default GeneralList;